<template>
   <div data-cy="autocomplete-select-v1" class="flex flex-col">
      <!-- Exclusive choices -->
      <div v-if="exclusiveOptions.length > 0" class="order-2 mt-2">
         <div class="mb-1 border-t border-grey-20"></div>
         <span class="sr-only">{{ $t("survey.autocomplete.exclusive_exp") }}</span>
         <ul>
            <li v-for="opt in exclusiveOptions" :key="opt.id">
               <CheckboxItem
                  :id="opt.id"
                  :label="opt.label"
                  :name="`${props.block.id}-exclusive-option`"
                  class="!mb-0 bg-white"
                  :checked="state.currentAnswer.choices[0]?.id === opt.id"
                  @change="changeExclusiveChoice"
               ></CheckboxItem>
            </li>
         </ul>
      </div>

      <!-- when max selections are reached, show the selected options without the multiselect field -->
      <div
         v-if="
            props.block.data?.maxChoices &&
            !state.exclusiveChoiceSelected &&
            state.currentAnswer.choices.length >= props.block.data?.maxChoices
         "
         data-cy="autocomplete-choices-selected"
      >
         <span class="sr-only">{{ $t("survey.autocomplete.selected_options") }}</span>
         <div
            v-for="option in state.currentAnswer.choices"
            :key="option.id"
            :data-cy="`selected-opt-${option.id}`"
            class="align-center my-2 flex justify-between rounded-lg bg-grey-10 px-4 py-1 text-base text-black"
         >
            <span class="py-2 leading-5">{{ option.label }}</span>
            <DefaultButton
               :aria-label="`${option.label} ${$t('survey.autocomplete.remove_option')}`"
               data-cy="remove"
               :disabled="state.exclusiveChoiceSelected"
               is-icon
               size="sm"
               style-type="ghost"
               rounded-edges="md"
               :color="Theme.Grey"
               @click="remove(option)"
            >
               <IconClose class="size-3 fill-grey-70" />
            </DefaultButton>
         </div>
      </div>

      <!-- Autocomplete combobox -->
      <multiselect
         v-else
         v-model="state.currentAnswer.choices"
         class="relative z-100"
         openDirection="bottom"
         :options="nonExclusiveOptions"
         :multiple="true"
         :loading="state.loadingChoices"
         :placeholder="placeholder"
         :max="props.block.data?.maxChoices"
         track-by="id"
         label="label"
         :disabled="state.exclusiveChoiceSelected"
         :deselectLabel="$t('survey.autocomplete.enter_to_remove')"
         :selectedLabel="$t('survey.autocomplete.selected')"
         :selectLabel="$t('survey.autocomplete.enter_to_select')"
         @select="addChoice"
         @remove="processInputs"
      >
         <template v-slot:selection="{ values, remove }">
            <div v-if="values.length === 0" data-cy="no-options-selected">
               <span class="sr-only">{{ $t("survey.autocomplete.no_selected_options") }}</span>
            </div>
            <div v-else data-cy="selected-options">
               <div v-if="!state.exclusiveChoiceSelected">
                  <span class="sr-only">{{ $t("survey.autocomplete.selected_options") }}</span>
                  <div
                     v-for="option in values"
                     :key="option.id"
                     :data-cy="`selected-opt-${option.id}`"
                     class="align-center my-2 flex justify-between rounded-lg bg-grey-10 px-4 py-1 text-base text-black"
                  >
                     <span class="py-2 leading-5">{{ option.label }}</span>
                     <DefaultButton
                        :aria-label="`${option.label} ${$t('survey.autocomplete.remove_option')}`"
                        data-cy="remove"
                        :disabled="state.exclusiveChoiceSelected"
                        is-icon
                        size="sm"
                        style-type="ghost"
                        rounded-edges="md"
                        :color="Theme.Grey"
                        @click="remove(option)"
                     >
                        <IconClose class="size-3 fill-grey-70" />
                     </DefaultButton>
                  </div>
               </div>
            </div>
         </template>
         <template v-slot:noResult>{{ $t("survey.autocomplete.no_results") }}</template>
         <template v-slot:maxElements>
            <span class="text-black">{{
               $t("survey.autocomplete.max_selected", { max: props.block.data.maxChoices })
            }}</span>
         </template>
      </multiselect>

      <!-- Remaining choices note -->
      <div style="min-height: 2rem">
         <div
            v-if="showRemainingChoicesHint"
            aria-live="polite"
            data-cy="remaining-choices"
            class="flex flex-row items-center p-3 text-xs leading-tight text-black"
         >
            <div class="shrink-0 pl-3 pr-4" aria-hidden="true">
               <IconAlert class="mr-auto block size-4 fill-grey-30" />
            </div>
            <span v-if="remainingChoices === 0">{{
               $t("survey.autocomplete.max_selected", { max: props.block.data.maxChoices })
            }}</span>
            <span v-else-if="remainingChoices === 1">{{ $t("survey.autocomplete.one_more_option") }}</span>
            <span v-else>{{ $t("survey.autocomplete.n_more_options", { number: remainingChoices }) }}</span>
         </div>
         <InvalidInputsList :errorList="errorList" :class="{ hidden: !showBlockErrors }" />
      </div>
   </div>
</template>

<script setup lang="ts">
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { AnswerResp } from "@/data/api-layer/answersApi";
import Multiselect from "vue-multiselect";
import { Theme } from "@/types";

export interface AutocompleteSelectAnswerData {
   choices: Choice[];
}

export interface AutocompleteSelectBlock extends FlowselBlock {
   data: {
      choices: Choice[];
      maxChoices?: number;
      placeholder?: string;
      showRemainingChoicesText?: boolean;
   };
}

interface Choice {
   id: string;
   label: string;
   isExclusive?: boolean;
}

interface Props {
   block: AutocompleteSelectBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

// declarations
// -----------------------------------------
const props = defineProps<Props>();
const { t: $t } = useI18n();
const emit = defineEmits(["update-answer", "update-valid"]);

const state = reactive({
   currentAnswer: {
      choices: props.userBlockAnswer?.data?.choices || [],
   } as AutocompleteSelectAnswerData,
   loadingChoices: false,
   exclusiveChoiceSelected: false,
});

// computed
// -----------------------------------------
const remainingChoices = computed(() => {
   if (!props.block.data?.maxChoices) return null;
   // If this is a multi-choice question but with a limit on maximum choices, show how many remain
   return props.block.data.maxChoices - state.currentAnswer.choices.length;
});

const placeholder = computed(() => {
   if (props.block.data?.placeholder) return props.block.data.placeholder;
   return $t("survey.autocomplete.placeholder");
});

const nonExclusiveOptions = computed(() => {
   return props.block.data.choices.filter((opt) => !opt.isExclusive);
});

const exclusiveOptions = computed(() => {
   return props.block.data.choices.filter((opt) => opt.isExclusive);
});

const showRemainingChoicesHint = computed(() => {
   return (
      (props.block.data?.maxChoices &&
         state.currentAnswer.choices.length > 0 &&
         props.block.data.showRemainingChoicesText &&
         !state.exclusiveChoiceSelected) ||
      (props.block.data?.maxChoices && state.currentAnswer.choices.length >= props.block.data.maxChoices)
   );
});

// validation - computed
// -----------------------------------------
const isAnswerValid = computed(() => {
   // There is no validation for this component except for (possibly) required
   if (props.block.isRequired && (!state.currentAnswer?.choices || state.currentAnswer.choices.length < 1))
      return false;
   else return true;
});

const errorList = computed(() => {
   if (!isAnswerValid.value)
      return [
         {
            inputId: props.block.id,
            validator: "required",
            message: $t("validation.choice.required"),
         },
      ];
   return [];
});

// methods
// -----------------------------------------
function addChoice(choice) {
   // If a non exclusive choice is selected, remove all exclusive choices
   if (exclusiveOptions.value.length && state.currentAnswer.choices.length > 0) {
      state.exclusiveChoiceSelected = false;
      const filteredChoices = state.currentAnswer.choices.filter((choice) => !choice.isExclusive);
      state.currentAnswer.choices = filteredChoices;
   }
   processInputs();
}

function changeExclusiveChoice({ newChoice, isChecked }) {
   if (isChecked) {
      // A choice may have the property 'isExclusive'. If an exclusive choice is selected, all other choices must be unselected
      state.currentAnswer.choices = [newChoice];
      state.exclusiveChoiceSelected = true;
   } else {
      // When removing an exclusive choice, we know that currentAnswer should return to [], as nothing else can have been selected at the same time
      state.currentAnswer.choices = [];
      state.exclusiveChoiceSelected = false;
   }
   processInputs();
}

function remove(option) {
   state.currentAnswer.choices = state.currentAnswer.choices.filter((choice) => choice.id !== option.id);
   processInputs();
}

function processInputs() {
   if (!isAnswerValid.value) {
      // an input is invalid or has errors
      emit("update-valid", false);
   } else {
      // all inputs are valid. This may be because there is a valid answer, or no answer
      // when this question is not required. For no answer, a different data format is required by the BE.
      let answerToSubmit = state.currentAnswer?.choices?.[0] ? state.currentAnswer : null;
      // Remove 'exclusive' property from choice before submitting as this will not be expected
      // (There can only ever be one choice if it is exclusive)
      if (state.currentAnswer?.choices?.[0]?.isExclusive) {
         answerToSubmit = {
            choices: [
               {
                  id: state.currentAnswer.choices[0].id,
                  label: state.currentAnswer.choices[0].label,
               },
            ],
         };
      }

      emit("update-valid", true);
      emit("update-answer", answerToSubmit);
   }
}

// This is needed on initialise because validity is being evaluated & answers updated on input
// so if you go back or already have an answer, it would not be automatically evaluated
processInputs();
</script>

<style scoped lang="scss"></style>
